import { SpotToast } from "@/components/SpotToast";
import { DateTime } from "luxon";

export const sortByProperty = (
  array: { [key: string]: any }[],
  property: string,
  isDesc: boolean = false
) => {
  return array.sort((a, b) => {
    if (!isDesc) {
      return a[property] > b[property] ? 1 : -1;
    } else if (isDesc) {
      return a[property] < b[property] ? 1 : -1;
    }
    return -1;
  });
};

export const capitalizeFirstLetter = (str?: string) => {
  if (str == null) {
    return undefined;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Turns a snake_case string ("first_name") into a title-cased label ("First Name").
export const snakeToTitleCase = (str: string): string =>
  str
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const getInitials = (name: string, amount: number = 2): string => {
  const words = name.trim().split(/\s+/); // Split by whitespace
  const initials = words
    .map((word) => (word.length ? word[0].toUpperCase() : "")) // Get the first letter of each word
    .slice(0, amount) // Take only the first two initials
    .join(""); // Combine them into a string
  return initials;
};

export const copyToClipboard = async (text?: string) => {
  try {
    await navigator.clipboard.writeText(text || "");
    SpotToast.success("Copied to clipboard");
  } catch {
    SpotToast.error("Could not copy to clipboard");
  }
};

const copyImgToClipboard = async (imgURL: string) => {
  try {
    const response = await fetch(imgURL);
    const blobData = await response.blob();
    const contentType = response.headers.get("Content-Type") || "image/png";

    const clipboardItemInput = new ClipboardItem({
      [contentType]: blobData,
    });
    navigator.clipboard.write([clipboardItemInput]);
    SpotToast.success("Copied image to clipboard");
  } catch (e) {
    SpotToast.error("There was an error. Could not copy image.");
  }
};

export function formatISOTime(isoTimestamp?: string) {
  if (isoTimestamp == null) {
    return undefined;
  }

  const date = new Date(isoTimestamp);

  if (!date) {
    return undefined;
  }

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

export function formatNumber(number?: string | number) {
  if (number == null) {
    return undefined;
  }

  const suffixes = ["", "k", "M", "B", "T"];
  let numValue = typeof number === "string" ? Number(number) : number;
  let i = 0;

  while (Math.abs(numValue) >= 1000 && i < suffixes.length - 1) {
    numValue /= 1000;
    i++;
  }

  // Format the number to 4 significant digits, including the suffix
  if (Number.isInteger(numValue)) {
    return `${numValue}${suffixes[i]}`;
  } else {
    return `${numValue.toPrecision(3)}${suffixes[i]}`; // Keeps 3 significant digits
  }
}

export function timeAgo(input: Date | string, language: string = "en") {
  // all dates come from the backend as UTC

  if (
    typeof input === "string" &&
    !input.endsWith("Z") &&
    !input.includes("+")
  ) {
    input += "Z";
  }
  const date = input instanceof Date ? input : new Date(input);
  const formatter = new Intl.RelativeTimeFormat(language);
  const ranges = {
    years: 3600 * 24 * 365,
    months: 3600 * 24 * 30,
    weeks: 3600 * 24 * 7,
    days: 3600 * 24,
    hours: 3600,
    minutes: 60,
    seconds: 1,
  };
  const secondsElapsed = (date.getTime() - Date.now()) / 1000;
  for (const key in ranges) {
    const rangesKey = key as keyof typeof ranges;
    if (ranges[rangesKey] < Math.abs(secondsElapsed)) {
      const delta = secondsElapsed / ranges[rangesKey];
      return formatter.format(Math.round(delta), rangesKey);
    }
  }
  return "Just now";
}

// Regex pattern for the format YYYY-MM-DDTHH:mm:ss optionally followed by a period and digits
const regexISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

function getOrdinalSuffix(day: number) {
  if (day % 10 === 1 && day !== 11) return "st";
  if (day % 10 === 2 && day !== 12) return "nd";
  if (day % 10 === 3 && day !== 13) return "rd";
  return "th";
}

/** Backend datetimes arrive as naive ISO strings that are actually UTC;
 * parse them as such so they render in the viewer's local time. */
export const parseUtcDate = (value: string | Date): Date => {
  if (value instanceof Date) return value;
  return new Date(regexISO.test(value) ? value + "Z" : value);
};

export function formatTime(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
  isOrdinal?: boolean
) {
  let finalDate = date;

  if (typeof finalDate === "string") {
    // Temporary while the info does not come from the backend with the Z
    finalDate = regexISO.test(finalDate) ? finalDate + "Z" : finalDate;
    finalDate = new Date(finalDate);
  }

  // Intl throws RangeError on an Invalid Date. Callers routinely pass an
  // optional field (`model.data_cutoff_date || ""`) and expect a `|| "-"`
  // fallback to cover it, so return empty rather than taking the page down.
  if (Number.isNaN(finalDate.getTime())) {
    return "";
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  };

  const formatter = new Intl.DateTimeFormat("en-US", options || defaultOptions);

  if (isOrdinal) {
    return formatter
      .format(finalDate)
      .replace(
        /\d+/,
        `${finalDate.getDate()}${getOrdinalSuffix(finalDate.getDate())}`
      );
  }

  return formatter.format(finalDate);
}

export function getDuration(time1: string | null, time2: string | null) {
  if (time1 == null || time2 == null) {
    return "-";
  }
  const date1 = parseUtcDate(time1);
  const date2 = parseUtcDate(time2);

  // Calculate the time difference in milliseconds
  const timeDiff = Math.abs(date2.getTime() - date1.getTime());
  if (!Number.isFinite(timeDiff)) return "-";

  // Convert time difference to hours, minutes, and seconds
  const hours = Math.floor(timeDiff / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

  // Construct the formatted string
  let result = "";
  if (hours !== 0) {
    result += `${hours} h `;
  }
  if (minutes !== 0 || hours !== 0) {
    result += `${minutes} min `;
  }
  result += `${seconds} sec`;

  return result.trim(); // Trim any leading/trailing spaces
}

function daysBetween(date: Date | string) {
  // Get the current date
  const currentDate = new Date();

  // Parse the input date
  const inputDate = new Date(date);

  // Calculate the difference in time (in milliseconds)
  const timeDifference = currentDate.getTime() - inputDate.getTime();

  // Convert the time difference from milliseconds to days
  const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

  return daysDifference;
}

function isToday(date: Date | string) {
  const today = new Date();
  const dateToCheck = new Date(date);
  return (
    dateToCheck.getDate() === today.getDate() &&
    dateToCheck.getMonth() === today.getMonth() &&
    dateToCheck.getFullYear() === today.getFullYear()
  );
}

function isYesterday(date: Date | string) {
  const dateToCheck = new Date(date);

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  return (
    dateToCheck.getDate() === yesterday.getDate() &&
    dateToCheck.getMonth() === yesterday.getMonth() &&
    dateToCheck.getFullYear() === yesterday.getFullYear()
  );
}

export function findMaxStringValue(
  arrayStrings: (string | undefined)[],
  regex = /^Output (\d+)$/
) {
  let maxNumber = 0; // Start with -1 to indicate no numbers found if that's the case

  arrayStrings.forEach((string) => {
    if (!string) return;
    const match = string.match(regex);
    if (match) {
      const number = parseInt(match[1], 10);
      if (number > maxNumber) {
        maxNumber = number;
      }
    }
  });

  return maxNumber;
}

function interpolateStringWithObject(originalString: string, obj: any) {
  if (typeof originalString !== "string" || typeof obj !== "object") {
    return "Invalid input. The originalString should be a string, and obj should be an object.";
  }
  Object.keys(obj).forEach((key) => {
    if (obj.hasOwnProperty(key)) {
      const specialKey = "{{" + key + "}}";
      const replacement = obj[key];

      originalString = originalString.replace(specialKey, replacement);
    }
  });

  return originalString;
}

export const formatUtcScheduleTime = (
  value: string,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  now = new Date()
): { label: string; tooltip?: string } | null => {
  const match = /^([01]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.exec(value);
  if (!match) return null;

  const utcDate = DateTime.fromJSDate(now, { zone: "UTC" }).set({
    hour: Number(match[1]),
    minute: Number(match[2]),
    second: Number(match[3] ?? 0),
    millisecond: 0,
  });
  const localDate = utcDate.setZone(timeZone);
  if (!localDate.isValid) return null;
  const timeFormat = match[3] ? "HH:mm:ss" : "HH:mm";
  const localTime = (date: DateTime) => {
    const localDay = date.toISODate()!;
    const utcDay = date.toUTC().toISODate()!;
    const dayShift =
      localDay > utcDay
        ? " (next day)"
        : localDay < utcDay
          ? " (previous day)"
          : "";
    return `${date.toFormat(timeFormat)}${dayShift}`;
  };

  // Sample the year rather than assuming northern-hemisphere seasons or a
  // one-hour DST shift. Include today's offset if a rule changed this year.
  const offsets = new Map<number, DateTime>([[localDate.offset, localDate]]);
  for (let month = 1; month <= 12; month++) {
    const sample = utcDate.set({ month, day: 15 }).setZone(timeZone);
    offsets.set(sample.offset, sample);
  }

  const current = `Runs at ${localTime(localDate)} in ${timeZone}`;
  if (offsets.size === 1) {
    return { label: `${current} all year.` };
  }

  const alternatives = [...offsets.values()]
    .filter((date) => date.offset !== localDate.offset)
    .map((date) => {
      const season = date.isInDST ? "during" : "outside";
      return `${localTime(date)} ${season} Daylight Saving Time`;
    });
  return { label: `${current}.`, tooltip: `${alternatives.join("; ")}.` };
};

export function formatSizeInBytes(bytes: number, decimalPlaces = 1) {
  const thresh = 1000;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const units = ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  let u = -1;
  const r = 10 ** decimalPlaces;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(decimalPlaces) + " " + units[u];
}

export enum URL_PROTOCOLS {
  HTTP = "http:",
  HTTPS = "https:",
}

export const isValidUrl = (
  url: string,
  options?: { allowed_protocols?: URL_PROTOCOLS[] }
) => {
  try {
    const urlObj = new URL(url);

    if (
      options?.allowed_protocols &&
      !options.allowed_protocols.some(
        (protocol) => protocol === urlObj.protocol
      )
    ) {
      return false;
    }
    return true;
  } catch (error) {
    return false;
  }
};

export const getWebsiteHostname = (urlString: string) => {
  try {
    const url = new URL(urlString);
    return url.hostname;
  } catch (e) {
    console.error("Invalid URL:", e);
    return urlString; // or handle the error as needed
  }
};

// Formats a value coming in MB to a compact string: 50.00 -> 50MB, 10200 -> 10GB
export function formatFromMB(mb?: number): string {
  if (mb == null || !isFinite(mb)) return "-";
  if (mb < 1024) return `${Math.round(mb)}MB`;
  return `${Math.round(mb / 1024)}GB`;
}

// Color utilities
export const isValidHexColor = (value: string) =>
  /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);

export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  let h = hex.replace("#", "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const num = parseInt(h, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

export const getRelativeLuminance = ({
  r,
  g,
  b,
}: {
  r: number;
  g: number;
  b: number;
}): number => {
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const R = toLinear(r);
  const G = toLinear(g);
  const B = toLinear(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

export const contrastRatio = (lum1: number, lum2: number) => {
  const L1 = Math.max(lum1, lum2);
  const L2 = Math.min(lum1, lum2);
  return (L1 + 0.05) / (L2 + 0.05);
};

export const rgbToHsl = ({ r, g, b }: { r: number; g: number; b: number }) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max - min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return { h, s, l };
};

export const hslToHex = ({
  h,
  s,
  l,
}: {
  h: number;
  s: number;
  l: number;
}): string => {
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (x: number) => {
    const v = Math.round(x * 255);
    return v.toString(16).padStart(2, "0");
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const adjustShadeBrightnessOnly = (
  hex: string,
  delta = 0.25
): string => {
  const { h, s, l } = rgbToHsl(hexToRgb(hex));
  const newL = Math.max(0, l - delta);
  return hslToHex({ h, s, l: newL });
};

export const pickUnifiedTextColor = (
  baseHex: string,
  shadeHex: string
): string => {
  const lumBase = getRelativeLuminance(hexToRgb(baseHex));
  const lumShade = getRelativeLuminance(hexToRgb(shadeHex));
  const lumWhite = 1;
  const lumBlack = getRelativeLuminance(hexToRgb("#060C11"));
  const minContrastWhite = Math.min(
    contrastRatio(lumWhite, lumBase),
    contrastRatio(lumWhite, lumShade)
  );
  const minContrastBlack = Math.min(
    contrastRatio(lumBlack, lumBase),
    contrastRatio(lumBlack, lumShade)
  );
  return minContrastWhite >= minContrastBlack ? "#ffffff" : "#060C11";
};

export const getFileImage = (
  content_type: string | null | undefined = "text/plain",
  source_type: string | null | undefined = undefined
) => {
  // TODO: Replace with API call to /api/groups/{group_id}/knowledge-base/mime-types
  // to get MIME type info including image URLs from the backend
  switch (content_type) {
    case "folder":
    case "application/vnd.google-apps.folder":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Folder.svg";
    case "application/pdf":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/PDF.svg";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/msword":
    case "application/vnd.google-apps.document":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Word.svg";
    case "application/json":
    case "text/plain":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Text.svg";
    case "text/markdown":
      if (source_type === "ServiceNow") {
        // return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/ServiceNow.svg";
        return "https://storage.googleapis.com/image-storage-spot-manual/logos/servicenow.png";
      }
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Text.svg";
    case "application/zip":
    case "application/x-zip-compressed":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Zip.svg";
    case "application/vnd.ms-powerpoint":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    case "application/vnd.google-apps.presentation":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Presentation.svg";
    case "text/csv":
    case "application/csv":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Excel.svg";
    case "application/vnd.ms-excel":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.google-apps.spreadsheet":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Excel.svg";
    case "sharepoint/page":
      return "https://storage.googleapis.com/image-storage-spot-manual/logos/sharepoint.png";
    case "text/html":
    case "website":
      return "https://storage.googleapis.com/image-storage-spot-manual/nodes/globe.svg";
    default:
      if (content_type?.startsWith("image/")) {
        return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/IMG.svg";
      } else if (content_type?.startsWith("audio/")) {
        return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/audio.svg";
      }
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Text.svg";
  }
};

export const getFolderImage = (
  content_type: string | null | undefined = "text/plain"
) => {
  switch (content_type) {
    case "folder":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Folder.svg";
    case "Google Drive":
    case "GoogleDrive":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/googeldrive-folder.svg";
    case "ServiceNow":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/servicenow-folder.svg";
    case "OneDrive":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/onedrive-folder.svg";
    case "Sharepoint":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/onedrive-folder.svg";
    case "website":
    case "Website":
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/website-folder.svg";
    default:
      return "https://storage.googleapis.com/image-storage-spot-manual/knowledge-bases/Folder.svg";
  }
};

export const normalizeValue = (
  value: number,
  array: number[],
  fakeMax?: number
) => {
  const max = Math.min(fakeMax || Math.max(...array), 300);
  const min = Math.min(...array);

  if (max === min) {
    return value;
  }

  return Math.floor(((value - min) / (max - min)) * 100);
};

const sigmifyTheValue = (
  value: number,
  array: number[],
  steepness: number = 20
) => {
  const max = Math.min(Math.max(...array), 300);
  const min = Math.min(...array);

  const rangeMid = (min + max) / 2;
  const logisticSigmoid = (x: number): number => 1 / (1 + Math.exp(-x));

  // Normalize value to range [-steepness, steepness] for smooth transition with sigmoid
  const normalizedValue = ((value - rangeMid) / (max - min)) * steepness;

  // Apply sigmoid function
  const scaledSigmoid = logisticSigmoid(normalizedValue);

  // Map sigmoid output [0, 1] to [0, 100]
  return scaledSigmoid * 100;
};

export const getClipboardFiles = (event: ClipboardEvent): FileList | null => {
  if (!event.clipboardData) return null;

  const items = Array.from(event.clipboardData.items); // Convert to array
  const allowedTypes = new Set([
    "application/pdf",
    "text/plain",
    "text/markdown",
    "application/json",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "text/csv",
    "text/html",
  ]);

  const dataTransfer = new DataTransfer();
  let hasFiles = false;

  for (const item of items) {
    if (item.kind === "file") {
      const file = item.getAsFile();
      if (
        file &&
        (allowedTypes.has(file.type) || file.type.startsWith("image/"))
      ) {
        dataTransfer.items.add(file);
        hasFiles = true;
      }
    }
  }

  return hasFiles ? dataTransfer.files : null;
};

export function softNavigateTo(url: string) {
  const router = window.__APP_ROUTER__;
  if (router?.replace) {
    router.replace(url); // soft navigation, no full reload
  } else {
    window.location.href = url; // fallback
  }
}

// Helper function to format numbers with precision
export const formatNumberWithSpaces = (numStr: string | number): string => {
  const numStrValue = typeof numStr === "number" ? numStr.toString() : numStr;
  const [intPart, decPart] = numStrValue.split(".");
  const spaced = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return decPart !== undefined ? `${spaced}.${decPart}` : spaced;
};

// Pulls the raw URI out of either shape the backend may return for an image
// field (`string` or `[{ uri: string }]`). Returns null when no usable value
// is present. Callers apply their own default and any URL transformation.
export const extractImageUri = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string" && value.length > 0) return value;
  if (Array.isArray(value) && value.length > 0 && value[0]?.uri) {
    return String(value[0].uri);
  }
  return null;
};

export const extractImageUrl = (value: unknown): string | null => {
  const uri = extractImageUri(value);
  if (!uri) return null;
  // String inputs are passed through unchanged to preserve existing call-site
  // behavior; only the array shape gets the spot:// → preview URL transform.
  if (typeof value === "string") return uri;
  if (uri.startsWith("http")) return uri;
  if (uri.startsWith("spot://")) {
    const fileId = uri.replace("spot://", "");
    return `/api/backend/file/${fileId}?preview=true`;
  }
  return uri;
};

// A stored file URI as a link that opens it: links pass through, a spot://
// file goes through the backend (or the public endpoint).
export const getSpotFileUrl = (
  uri: string,
  isPreview = false,
  isPublic = false
) => {
  if (
    uri.startsWith("https://") ||
    uri.startsWith("http://") ||
    uri.startsWith("data:")
  )
    return uri;
  const fileId = uri.replace("spot://", "");
  const endpoint = isPublic
    ? `/api/public/file/public/${fileId}`
    : `/api/backend/file/${fileId}`;
  return `${endpoint}?preview=${isPreview}`;
};

// The file id in a spot:// URI, or null for anything else.
export function spotFileId(uri: string): string | null {
  if (!uri.startsWith("spot://")) return null;
  const id = uri.slice("spot://".length);
  return UUID_RE.test(id) ? id : null;
}

export const getPublicSpotFileUrl = (uri: string, isPreview = false) => {
  if (uri.startsWith("https://") || uri.startsWith("http://")) return uri;
  return `/api/public/file/public/${uri.replace("spot://", "")}?preview=${isPreview}`;
};

export function isValidSvgString(svgString: any) {
  if (typeof svgString !== "string") return false;
  try {
    // 1. Create a DOMParser instance
    const parser = new DOMParser();

    // 2. Attempt to parse the string as an XML/SVG document
    // We use 'image/svg+xml' MIME type for SVG parsing
    const doc = parser.parseFromString(svgString, "image/svg+xml");

    // 3. Check for parser errors
    // The parser inserts a <parsererror> element into the document
    // if the XML/SVG is not well-formed.
    const isError = doc.getElementsByTagName("parsererror").length > 0;

    // 4. Additionally, check if the root element is actually <svg>
    const isSvg = doc.documentElement.nodeName === "svg";

    // A valid-looking SVG should have no parser errors AND the root element should be <svg>
    return !isError && isSvg;
  } catch (e) {
    // Catch any unexpected runtime errors (though less common with DOMParser)
    console.error("Error during SVG validation:", e);
    return false;
  }
}
// macOS only; Linux and Windows get the Ctrl spelling of a shortcut.
export const isMac = (): boolean =>
  typeof navigator !== "undefined" &&
  navigator.platform.toUpperCase().indexOf("MAC") >= 0;

export const getModifierKey = (): string => {
  if (typeof window === "undefined") return "⌘";

  const platform = window.navigator.platform.toLowerCase();
  const userAgent = window.navigator.userAgent.toLowerCase();

  return platform.includes("win") || userAgent.includes("windows")
    ? "Ctrl"
    : "⌘";
};

export const getColorFromTheme = (color: string) => {
  // Handle arbitrary hex like text-[#ff00aa] -> return "ff00aa"
  const hex = color.match(/text-\[#([0-9a-fA-F]{3,8})\]/)?.[1];
  return hex ? `#${hex}` : "";
};

// A deployment path segment is either a uuid or a human alias; uuid-shaped
// aliases are rejected server-side, so the shape discriminates.
export const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isExponentKey = (key: string) =>
  key === "e" || key === "E" || key === "+";
